import { useMutation } from "@tanstack/react-query";
import { useContext } from "react";
import {
  AlertBannerContext,
  ALERT_BANNER_CODE,
  ALERT_BANNER_CODE_SEVERITY,
  API_RESPONSE_CODE
} from "@kivunova/kivufrontendcommon";
import { fetchGraphQL } from "../../Graphql/utils";
import { resetPasswordQuery } from "../../Graphql/querries/resetPasswordQuery";
import {
  ResetPasswordRequest,
  ResetPasswordResponse
} from "../../Graphql/types/resetPassword";

import {
  LOG_LEVEL,
  buildLogContext,
  captureAppException,
  sendAppLog
} from "../../Utils/appLogger";

const useResetPassword = () => {
  const { setAlert } = useContext(AlertBannerContext);

  return useMutation<ResetPasswordResponse, Error, ResetPasswordRequest>({
    mutationKey: ["reset-password"],

    mutationFn: (input) => {
      sendAppLog(
        LOG_LEVEL.INFO,
        "Started resetting password",
        buildLogContext(
          "auth",
          "reset-password",
          // userId/companyId/storeId are not known — user is unauthenticated at this point
          undefined,
          undefined,
          undefined
        )
      );
      return fetchGraphQL(resetPasswordQuery, { input }, setAlert);
    },

    onSuccess: (response) => {
      const { code } = response.resetPassword;

      if (code === API_RESPONSE_CODE.OK) {
        // ✅ Success
        sendAppLog(
          LOG_LEVEL.INFO,
          "Password reset successfully",
          buildLogContext(
            "auth",
            "reset-password",
            // userId/companyId/storeId not available — reset token flow, user not yet signed in
            undefined,
            undefined,
            undefined
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.SUCCESS,
          code: ALERT_BANNER_CODE.PASWORD_RESET_SUCCESSFULLY
        });
      } else {
        // ⚠️ Expected API/business failure
        sendAppLog(
          LOG_LEVEL.WARNING,
          "Password reset rejected by API",
          buildLogContext(
            "auth",
            "reset-password",
            // userId/companyId/storeId not available — reset token flow, user not yet signed in
            undefined,
            undefined,
            undefined,
            { apiCode: String(code) }
          )
        );

        setAlert({
          severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
          code: code as ALERT_BANNER_CODE
        });
      }
    },

    onError: (error) => {
      // ❌ Unexpected runtime/network error
      captureAppException(
        "Password reset failed with unexpected error",
        error,
        buildLogContext(
          "auth",
          "reset-password",
          // userId/companyId/storeId not available — reset token flow, user not yet signed in
          undefined,
          undefined,
          undefined
        )
      );

      setAlert({
        severity: ALERT_BANNER_CODE_SEVERITY.ERROR,
        code: error?.message
          ? (error.message as ALERT_BANNER_CODE)
          : ALERT_BANNER_CODE.INTERNAL_SERVER_ERROR
      });
    }
  });
};

export default useResetPassword;
